import { createContext, useContext } from 'react'

/**
 * Envio de arquivo direto ao Storage, por URL assinada (docs/api/README.md; docs/banco/modelagem.md
 * §13). A API reserva o envio e devolve para onde e como mandar; o arquivo vai do navegador ao
 * Storage sem passar pela API. É a única chamada de rede fora do cliente de `./api`: a URL já vem
 * assinada, então não leva o token da sessão. Nenhuma página chama `fetch`: o `api.ts` da área usa
 * `useUploader()`, e testes e stories trocam o envio por um falso (T25).
 */

/** O que a API devolve ao reservar um envio. */
export interface UploadTarget {
  uploadUrl: string
  method: 'PUT'
  /** Cabeçalhos que o envio precisa repetir, como o `Content-Type` declarado na reserva. */
  headers: Record<string, string>
}

export type UploadErrorCode =
  /** Não houve resposta: sem conexão ou envio interrompido. */
  | 'network_error'
  /** O arquivo passa do limite do destino (413). */
  | 'too_large'
  /** O tipo do arquivo não é aceito no destino (415). */
  | 'unsupported_type'
  /** Qualquer outra recusa do Storage. */
  | 'refused'

const MESSAGES: Record<UploadErrorCode, string> = {
  network_error:
    'Não conseguimos enviar o arquivo. Confira a conexão e tente de novo; nada foi perdido.',
  too_large: 'O arquivo passa do tamanho aceito. Envie um arquivo menor.',
  unsupported_type: 'Este tipo de arquivo não é aceito. Envie em outro formato.',
  refused: 'O envio do arquivo foi recusado. Tente de novo em instantes.',
}

/** A falha do envio, com um código para a tela escolher a frase da sua área. */
export class UploadError extends Error {
  readonly code: UploadErrorCode
  /** Status HTTP da recusa; 0 quando não houve resposta. */
  readonly status: number

  constructor(code: UploadErrorCode, status = 0) {
    super(MESSAGES[code])
    this.name = 'UploadError'
    this.code = code
    this.status = status
  }
}

export function isUploadError(error: unknown): error is UploadError {
  return error instanceof UploadError
}

export type Uploader = (target: UploadTarget, file: Blob) => Promise<void>

function codeOf(status: number): UploadErrorCode {
  if (status === 413) return 'too_large'
  if (status === 415) return 'unsupported_type'
  return 'refused'
}

/** O envio de verdade: o arquivo inteiro no corpo, com o método e os cabeçalhos da reserva. */
export function createUploader(doFetch?: typeof fetch): Uploader {
  return async (target, file) => {
    const send = doFetch ?? globalThis.fetch.bind(globalThis)
    let response: Response
    try {
      response = await send(target.uploadUrl, {
        method: target.method,
        headers: target.headers,
        body: file,
      })
    } catch {
      throw new UploadError('network_error')
    }
    if (!response.ok) throw new UploadError(codeOf(response.status), response.status)
  }
}

/** O envio ao alcance das áreas. Sem provedor, vale o de verdade; teste e story entregam um falso. */
export const UploaderContext = createContext<Uploader>(createUploader())

export function useUploader(): Uploader {
  return useContext(UploaderContext)
}
